/**
 * AuthContext — provides authentication state to the entire app.
 *
 * Wraps Cognito session management with a simple React context:
 *   - user (email + token) or null
 *   - login / signup / confirm / logout functions
 *   - loading state (true while checking stored session on mount)
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  signIn,
  signUp,
  confirmSignUp,
  signOut,
  getCurrentSession,
  getIdToken,
} from "./cognito";
import { identifyUser, resetPostHog } from "@/lib/posthog";
import { setSentryUser } from "@/lib/sentry";

const UI_ONLY_MODE =
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "1" ||
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "true";

// ── Types ───────────────────────────────────────────

export interface AuthUser {
  email: string;
  token: string;
  firstName: string;
  lastName: string;
  userId?: string;
  tenantId?: string | null;
  needsOnboarding?: boolean;
  role?: string;
  permissionLevel?: string;
  onboardingCompleted?: boolean;
  discoveryStatus?: string;
  jobTitle?: string | null;
  department?: string | null;
  phone?: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  needsOnboarding: boolean;
  inviteCode: string;
  setInviteCode: (code: string) => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phone: string,
  ) => Promise<void>;
  confirm: (email: string, code: string) => Promise<void>;
  logout: () => void;
  getToken: () => Promise<string | null>;
  updateFirstName: (name: string) => void;
  markOnboarded: (userId: string, tenantId: string, role: string) => void;
  setDiscoveryStatus: (status: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(
    UI_ONLY_MODE
      ? {
          email: "owen@askforeman.co.uk",
          token: "",
          firstName: "Owen",
          lastName: "Dev",
          userId: "demo",
          tenantId: "00000000-0000-0000-0000-000000000001",
          role: "owner",
          needsOnboarding: false,
        }
      : null,
  );
  const [loading, setLoading] = useState(!UI_ONLY_MODE);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [inviteCode, setInviteCode] = useState("");

  // Check for existing session on mount
  useEffect(() => {
    if (UI_ONLY_MODE) {
      setLoading(false);
      return;
    }
    getCurrentSession()
      .then(async (session) => {
        if (session) {
          const token = session.getIdToken().getJwtToken();
          const payload = session.getIdToken().payload;
          const email =
            payload["email"] ?? payload["cognito:username"] ?? "user";
          const firstName =
            payload["given_name"] ?? payload["name"]?.split(" ")[0] ?? "";
          const lastName = payload["family_name"] ?? "";
          setUser({ email, token, firstName, lastName });

          // Check user profile status
          try {
            const res = await fetch("/api/me", {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const profile = await res.json();
              setNeedsOnboarding(profile.needs_onboarding ?? false);
              if (!profile.needs_onboarding && profile.user_id) {
                identifyUser(profile.user_id, {
                  email,
                  tenantId: profile.tenant_id,
                  role: profile.role,
                });
                setSentryUser({ id: profile.user_id, email });
                setUser((prev) =>
                  prev
                    ? {
                        ...prev,
                        userId: profile.user_id,
                        tenantId: profile.tenant_id,
                        role: profile.role,
                        needsOnboarding: false,
                        permissionLevel: profile.permission_level,
                        onboardingCompleted:
                          profile.onboarding_completed ?? false,
                        discoveryStatus: profile.discovery_status ?? "pending",
                        jobTitle: profile.job_title ?? null,
                        department: profile.department ?? null,
                        phone: profile.phone ?? null,
                      }
                    : null,
                );
              }
            }
          } catch {
            // Profile check failed — non-blocking
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    if (UI_ONLY_MODE) {
      setUser({
        email,
        token: "",
        firstName: email.split("@")[0] || "Local",
        lastName: "",
      });
      return;
    }
    const session = await signIn(email, password);
    const token = session.getIdToken().getJwtToken();
    const payload = session.getIdToken().payload;
    const userEmail = payload["email"] ?? payload["cognito:username"] ?? email;
    const firstName =
      payload["given_name"] ?? payload["name"]?.split(" ")[0] ?? "";
    const lastName = payload["family_name"] ?? "";

    // Create user record in DB + check onboarding status BEFORE setting user
    // (setting user triggers the useEffect redirect in LoginPage, so
    // needsOnboarding must be resolved first to avoid a race condition)
    let userFields: Partial<AuthUser> = {};
    try {
      const res = await fetch("/api/me", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const onboard = data.needs_onboarding ?? data.tenant_id === null;
        setNeedsOnboarding(onboard);
        userFields = {
          userId: data.user_id,
          tenantId: data.tenant_id,
          role: data.role ?? undefined,
          needsOnboarding: onboard,
          permissionLevel: data.permission_level,
          onboardingCompleted: data.onboarding_completed ?? false,
          discoveryStatus: data.discovery_status ?? "pending",
        };
      }
    } catch {
      // Non-blocking — worst case user will see onboarding
    }

    setUser({ email: userEmail, token, firstName, lastName, ...userFields });
    if (userFields.userId) {
      identifyUser(userFields.userId, {
        email: userEmail,
        tenantId: userFields.tenantId,
        role: userFields.role,
      });
      setSentryUser({ id: userFields.userId, email: userEmail });
    }
  }, []);

  const signup = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      phone: string,
    ) => {
      if (UI_ONLY_MODE) return;
      await signUp(email, password, firstName, lastName, phone);
    },
    [],
  );

  const confirm = useCallback(async (email: string, code: string) => {
    if (UI_ONLY_MODE) return;
    await confirmSignUp(email, code);
  }, []);

  const logout = useCallback(() => {
    if (UI_ONLY_MODE) {
      setUser(null);
      return;
    }
    signOut();
    resetPostHog();
    setSentryUser(null);
    setUser(null);
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (UI_ONLY_MODE) return null;
    const token = await getIdToken();
    if (token && user) {
      // Refresh the stored token silently
      setUser((prev) => (prev ? { ...prev, token } : null));
    }
    return token;
  }, [user]);

  const updateFirstName = useCallback((name: string) => {
    setUser((prev) => (prev ? { ...prev, firstName: name } : prev));
  }, []);

  const markOnboarded = useCallback(
    (userId: string, tenantId: string, role: string) => {
      setNeedsOnboarding(false);
      setUser((prev) =>
        prev
          ? {
              ...prev,
              userId,
              tenantId,
              role,
              needsOnboarding: false,
              onboardingCompleted: false,
              discoveryStatus: "pending",
            }
          : null,
      );
    },
    [],
  );

  const setDiscoveryStatus = useCallback((status: string) => {
    setUser((prev) => (prev ? { ...prev, discoveryStatus: status } : prev));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        needsOnboarding,
        inviteCode,
        setInviteCode,
        login,
        signup,
        confirm,
        logout,
        getToken,
        updateFirstName,
        markOnboarded,
        setDiscoveryStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
